import { useEffect, useReducer, useState } from 'react';
import { Icon, type IconName } from '@garnet/ui';
import { FixtureApi } from './data/api.js';
import type { CoreStudioApi } from './data/api.js';
import type { Permission, RoleKey } from './data/types.js';
import { useHashRoute } from './router.js';
import { type RuleMode, type StudioContext } from './studio.js';
import { TWEAK_DEFAULTS, TweaksPanel, type Tweaks } from './shell/TweaksPanel.js';
import { Dashboard } from './screens/Dashboard.js';
import { RuleStore } from './screens/RuleStore.js';
import { RuleDetail } from './screens/RuleDetail.js';
import { Coverage } from './screens/Coverage.js';
import { Classifier } from './screens/Classifier.js';
import { Audit } from './screens/Audit.js';
import { Sources } from './screens/Sources.js';

const VIEWS = ['dashboard', 'store', 'rule', 'coverage', 'classifier', 'audit', 'sources'] as const;
type View = (typeof VIEWS)[number];

interface NavItemDef {
  key: View;
  label: string;
  short: string;
  icon: IconName;
}
const NAV: { group: string; items: NavItemDef[] }[] = [
  { group: 'Client', items: [{ key: 'dashboard', label: 'Dashboard', short: 'Home', icon: 'layers' }] },
  {
    group: 'Corpus',
    items: [
      { key: 'store', label: 'Coverage matrix', short: 'Matrix', icon: 'grid' },
      { key: 'sources', label: 'Source of law', short: 'Sources', icon: 'book' },
    ],
  },
  {
    group: 'Serving',
    items: [
      { key: 'coverage', label: 'Coverage & abstention', short: 'Gate', icon: 'map' },
      { key: 'classifier', label: 'Classification', short: 'Classify', icon: 'scale' },
    ],
  },
  { group: 'History', items: [{ key: 'audit', label: 'Change & audit', short: 'Change', icon: 'branch' }] },
];

const TWEAK_KEY = 'garnetcore.tweaks.v1';

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(TWEAK_KEY);
    if (raw) return { ...TWEAK_DEFAULTS, ...(JSON.parse(raw) as Partial<Tweaks>) };
  } catch {
    /* ignore */
  }
  return TWEAK_DEFAULTS;
}

function Wordmark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 6,
          background: 'var(--garnet)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 17, color: '#fff', fontWeight: 600, lineHeight: 1 }}>§</span>
      </div>
      <div style={{ lineHeight: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span className="serif" style={{ fontSize: 17 }}>
            Garnet
          </span>
          <span className="mono" style={{ fontSize: 14, color: 'var(--fg-2)', fontWeight: 500 }}>
            Core
          </span>
        </div>
        <div className="label" style={{ fontSize: 7.5, marginTop: 2 }}>
          back office · rule store
        </div>
      </div>
    </div>
  );
}

function NavButton({
  item,
  active,
  onClick,
  mode,
}: {
  item: NavItemDef;
  active: boolean;
  onClick: () => void;
  mode: Tweaks['shell'];
}) {
  if (mode === 'rail') {
    return (
      <button
        onClick={onClick}
        title={item.label}
        aria-pressed={active}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          padding: '9px 4px',
          width: '100%',
          borderRadius: 6,
          position: 'relative',
          background: active ? 'var(--garnet-muted)' : 'transparent',
          color: active ? 'var(--garnet)' : 'var(--fg-2)',
        }}
      >
        {active && <span style={{ position: 'absolute', left: -8, top: 8, bottom: 8, width: 2, background: 'var(--garnet)', borderRadius: 2 }} />}
        <Icon name={item.icon} size={17} />
        <span className="nav-label" style={{ fontSize: 8, textAlign: 'center', lineHeight: 1.1, letterSpacing: '0.02em' }}>
          {item.short}
        </span>
      </button>
    );
  }
  if (mode === 'command') {
    return (
      <button
        onClick={onClick}
        aria-pressed={active}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '6px 11px',
          borderRadius: 5,
          background: active ? 'var(--garnet-muted)' : 'transparent',
          color: active ? 'var(--garnet)' : 'var(--fg-2)',
          border: '1px solid ' + (active ? 'var(--garnet)' : 'transparent'),
        }}
      >
        <Icon name={item.icon} size={14} />
        <span className="nav-label" style={{ fontSize: 12, fontWeight: 500 }}>
          {item.label}
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 11px',
        width: '100%',
        borderRadius: 6,
        position: 'relative',
        textAlign: 'left',
        background: active ? 'var(--garnet-muted)' : 'transparent',
        color: active ? 'var(--garnet)' : 'var(--fg-2)',
      }}
    >
      {active && <span style={{ position: 'absolute', left: -8, top: 7, bottom: 7, width: 2, background: 'var(--garnet)', borderRadius: 2 }} />}
      <Icon name={item.icon} size={15} />
      <span className="nav-label" style={{ fontSize: 12.5, fontWeight: active ? 500 : 400 }}>
        {item.label}
      </span>
    </button>
  );
}

function Divider() {
  return <span style={{ width: 1, height: 14, background: 'var(--border)' }} />;
}

function StatusLine({ ctx }: { ctx: StudioContext }) {
  const m = ctx.api.meta();
  const roleLabel = ctx.api.roles()[ctx.role].label;
  const seg = (label: string, value: string, tone?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span className="label" style={{ fontSize: 8.5 }}>
        {label}
      </span>
      <span className="mono" style={{ fontSize: 11, color: tone || 'var(--fg-1)' }}>
        {value}
      </span>
    </div>
  );
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 16px',
        height: 30,
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        overflowX: 'auto',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="dot" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--status-pass)' }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--status-pass-text)' }}>
          rule store · current
        </span>
      </div>
      <Divider />
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
        role="button"
        onClick={() => ctx.openRule('EU-DualUse-Annex1-9A')}
        title="one uncited draft is holding this under 100%"
      >
        <span className="label" style={{ fontSize: 8.5 }}>
          citations complete
        </span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--status-warn-text)' }}>
          {m.citationCompleteness}%
        </span>
        <Icon name="alert" size={10} color="var(--status-warn-text)" />
      </div>
      <Divider />
      {seg('abstains', m.abstainRate + '%', 'var(--abstain-text)')}
      <Divider />
      {seg('not covered', m.noCoverageRate + '%')}
      <Divider />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="shield" size={11} color="var(--garnet)" />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
          human-authored · no AI
        </span>
      </div>
      <Divider />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="label" style={{ fontSize: 8.5 }}>
          you are
        </span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--garnet)' }}>
          {roleLabel}
        </span>
      </div>
      <div style={{ flex: 1 }} />
      {seg('release', m.release, 'var(--garnet)')}
    </div>
  );
}

export function App({ api: injected }: { api?: CoreStudioApi } = {}) {
  const [api, setApi] = useState<CoreStudioApi>(() => injected ?? new FixtureApi());
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);

  const [view, nav] = useHashRoute<View>(VIEWS, 'dashboard');
  const [selectedCell, setSelectedCell] = useState('EU:medical');
  const [activeRule, setActiveRule] = useState('EU-MDR-AnnexVIII-R12');
  const [ruleMode, setRuleMode] = useState<RuleMode>('read');
  const [diffRule, setDiffRule] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [role, setRole] = useState<RoleKey>('reviewer');
  const [clientId, setClientId] = useState('meridian');

  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute('data-theme', tweaks.theme === 'dark' ? 'dark' : 'light');
    el.setAttribute('data-density', tweaks.density);
    el.setAttribute('data-accent', tweaks.accent);
    el.setAttribute('data-mono', tweaks.mono === 'forward' ? 'forward' : 'balanced');
  }, [tweaks]);

  function setTweak<K extends keyof Tweaks>(key: K, value: Tweaks[K]) {
    setTweaks((prev) => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem(TWEAK_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  const roles = api.roles();
  const people = api.people();
  const can = (perm: Permission): boolean => !!roles[role].can[perm];
  const author = people[role].name;
  const currentClient = () => api.clients().find((c) => c.id === clientId) ?? api.clients()[0]!;

  const openRule = (id: string, mode: RuleMode = 'read') => {
    setActiveRule(id);
    setRuleMode(mode);
    nav('rule');
  };
  const openSource = (id: string) => {
    setSelectedSource(id);
    nav('sources');
  };
  const openDiff = (id: string) => {
    setDiffRule(id);
    nav('audit');
  };
  const openStore = (cell: string) => {
    setSelectedCell(cell);
    nav('store');
  };
  const createRule = (juris: string, domain: string) => {
    if (!can('author')) return;
    const r = api.createRule(juris, domain, author);
    bump();
    openRule(r.ruleId, 'author');
  };

  const ctx: StudioContext = {
    api,
    role,
    clientId,
    author,
    can,
    currentClient,
    bump,
    openRule,
    openSource,
    openDiff,
    openStore,
    goView: (v) => nav(v as View),
    createRule,
  };

  const changeClient = (id: string) => {
    setClientId(id);
    if (view === 'rule') nav('dashboard');
  };

  const onReset = () => {
    setApi(new FixtureApi());
    setActiveRule('EU-MDR-AnnexVIII-R12');
    setRuleMode('read');
    setSelectedCell('EU:medical');
    setSelectedSource(null);
    setDiffRule(null);
    setRole('reviewer');
    setClientId('meridian');
    setTweaks(TWEAK_DEFAULTS);
    try {
      localStorage.removeItem(TWEAK_KEY);
    } catch {
      /* ignore */
    }
    nav('dashboard');
    bump();
  };

  const mode = tweaks.shell;
  const activeNav: View = view === 'rule' ? 'store' : view;
  const railWidth = mode === 'rail' ? 66 : 222;

  let surface: React.ReactNode = null;
  if (view === 'dashboard') surface = <Dashboard ctx={ctx} />;
  else if (view === 'store') surface = <RuleStore ctx={ctx} selectedCell={selectedCell} setSelectedCell={setSelectedCell} />;
  else if (view === 'rule') surface = <RuleDetail ctx={ctx} ruleId={activeRule} mode={ruleMode} setMode={setRuleMode} />;
  else if (view === 'coverage') surface = <Coverage ctx={ctx} />;
  else if (view === 'classifier') surface = <Classifier ctx={ctx} />;
  else if (view === 'audit') surface = <Audit ctx={ctx} initialDiffRule={diffRule} />;
  else if (view === 'sources') surface = <Sources ctx={ctx} selectedSource={selectedSource} setSelectedSource={setSelectedSource} />;

  const person = people[role];
  const roleObj = roles[role];
  const client = currentClient();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-base)' }}>
      {/* context bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '0 16px',
          height: 56,
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
        }}
      >
        <Wordmark />
        {mode === 'command' && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', paddingLeft: 8, borderLeft: '1px solid var(--border-subtle)' }}>
            {NAV.flatMap((g) => g.items).map((item) => (
              <NavButton key={item.key} item={item} active={activeNav === item.key} onClick={() => nav(item.key)} mode="command" />
            ))}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {can('author') && (
          <button
            className="btn btn-primary btn-sm mono"
            onClick={() => createRule(client.jurisdictions[0] ?? 'EU', client.domains[0] ?? 'medical')}
          >
            <Icon name="plus" size={12} />
            New rule
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', background: 'var(--bg-sunken)', border: '1px solid var(--border)', borderRadius: 5 }}>
          <Icon name="layers" size={14} color="var(--garnet)" />
          <div style={{ lineHeight: 1.2 }}>
            <div className="label" style={{ fontSize: 7.5 }}>
              client
            </div>
            <select
              value={clientId}
              onChange={(e) => changeClient(e.target.value)}
              aria-label="Switch client"
              style={{ background: 'transparent', border: 'none', color: 'var(--fg-1)', fontSize: 12.5, fontWeight: 600, outline: 'none', cursor: 'pointer', padding: 0, maxWidth: 150 }}
            >
              {api.clients().map((cl) => (
                <option key={cl.id} value={cl.id}>
                  {cl.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ width: 1, height: 28, background: 'var(--border)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontSize: 12, color: 'var(--fg-1)' }}>{person.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span className="label" style={{ fontSize: 8, color: 'var(--garnet)' }}>
                {roleObj.label}
              </span>
              <span className="label" style={{ fontSize: 8, color: 'var(--fg-3)' }}>
                · {person.title}
              </span>
            </div>
          </div>
          <div style={{ position: 'relative', width: 30, height: 30 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span className="mono" style={{ fontSize: 12, color: 'var(--garnet)' }}>
                {person.initials}
              </span>
            </div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleKey)}
              title="Switch role"
              aria-label="Switch role"
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
            >
              {Object.entries(roles).map(([k, r]) => (
                <option key={k} value={k}>
                  {r.label} — {people[k as RoleKey].name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* middle */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {mode !== 'command' && (
          <div
            style={{
              width: railWidth,
              flexShrink: 0,
              borderRight: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              display: 'flex',
              flexDirection: 'column',
              padding: mode === 'rail' ? '12px 8px' : '14px 12px',
              gap: mode === 'rail' ? 14 : 18,
              overflowY: 'auto',
            }}
          >
            {NAV.map((g) => (
              <div key={g.group} style={{ display: 'flex', flexDirection: 'column', gap: mode === 'rail' ? 4 : 3 }}>
                {mode === 'list' && (
                  <div className="label" style={{ fontSize: 8.5, padding: '0 4px 4px' }}>
                    {g.group}
                  </div>
                )}
                {mode === 'rail' && <div style={{ height: 1, background: 'var(--border-subtle)', margin: '0 6px' }} />}
                {g.items.map((item) => (
                  <NavButton key={item.key} item={item} active={activeNav === item.key} onClick={() => nav(item.key)} mode={mode} />
                ))}
              </div>
            ))}
            <div style={{ flex: 1 }} />
            {mode === 'list' &&
              (() => {
                const cov = api.coverageList();
                const covered = cov.filter((c) => c.state === 'covered').length;
                const pct = cov.length ? Math.round((covered / cov.length) * 100) : 0;
                return (
                  <div style={{ padding: 10, background: 'var(--bg-sunken)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                    <div className="label" style={{ fontSize: 8, marginBottom: 6 }}>
                      Coverage
                    </div>
                    <div className="serif" style={{ fontSize: 24, color: 'var(--garnet)' }}>
                      {pct}%
                    </div>
                    <div className="metatext" style={{ fontSize: 10 }}>
                      {covered} of {cov.length} cells covered
                    </div>
                  </div>
                );
              })()}
          </div>
        )}

        <div
          className="scroll-y"
          style={{ flex: 1, minWidth: 0, padding: 'var(--space-6)', background: 'var(--bg-base)', ['--chrome' as string]: '150px' }}
        >
          <div style={{ maxWidth: 1440, margin: '0 auto', width: '100%' }}>{surface}</div>
        </div>
      </div>

      <StatusLine ctx={ctx} />

      <TweaksPanel tweaks={tweaks} setTweak={setTweak} onReset={onReset} />
    </div>
  );
}
